// Enhances <pre><code> blocks inside post content with a language label, copy button
// and syntax highlighting (Prism, languages below; any other language stays plain).

import "./prismManual";
import Prism from "prismjs";
import "prismjs/components/prism-python";
import "prismjs/components/prism-bash";
import "prismjs/components/prism-json";
import "prismjs/components/prism-typescript";
import "prismjs/components/prism-yaml";

const SVG_NS = "http://www.w3.org/2000/svg";
// Подсветка — самая тяжёлая часть, а блоков в гайде бывает под двадцать. Красим блок,
// когда до него остаётся примерно экран прокрутки, а не все сразу при загрузке.
const HIGHLIGHT_AHEAD = "800px 0px";

let highlighter = null;

function svg(paths) {
    const el = document.createElementNS(SVG_NS, "svg");
    el.setAttribute("viewBox", "0 0 24 24");
    el.setAttribute("aria-hidden", "true");
    paths.forEach(([tag, attrs]) => {
        const child = document.createElementNS(SVG_NS, tag);
        for (const [k, v] of Object.entries(attrs)) child.setAttribute(k, v);
        el.appendChild(child);
    });
    return el;
}

function copyIcon() {
    return svg([
        ["rect", { x: "9", y: "9", width: "11", height: "11", rx: "2", ry: "2" }],
        ["path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" }],
    ]);
}

function checkIcon() {
    return svg([["polyline", { points: "20 6 9 17 4 12" }]]);
}

function setButton(btn, icon, text) {
    btn.replaceChildren();
    btn.appendChild(icon);
    const span = document.createElement("span");
    span.className = "th-code__copy-label";
    span.textContent = text;
    btn.appendChild(span);
}

function detectLanguage(codeEl) {
    const cls = codeEl.className || "";
    const match = cls.match(/language-([\w-]+)/i);
    if (match) return match[1].toLowerCase();
    const parent = codeEl.closest("figure.kg-code-card");
    if (parent) {
        const figcaption = parent.querySelector("figcaption");
        if (figcaption && figcaption.textContent.trim()) return figcaption.textContent.trim();
    }
    return "code";
}

function enhance(pre) {
    const code = pre.querySelector("code");
    if (!code) return;
    // Уже обёрнут (возврат из bfcache): старый наблюдатель отключён, поэтому блоки,
    // до которых не дошла прокрутка, снова ставим в очередь на подсветку.
    if (pre.closest(".th-code")) {
        if (code.dataset.thHighlight === "pending") scheduleHighlight(code);
        return;
    }

    const lang = detectLanguage(code);
    if (Prism.languages[lang]) {
        // Язык мог прийти из подписи карточки, а Prism читает только класс language-*.
        code.classList.add(`language-${lang}`);
        scheduleHighlight(code);
    }

    const wrap = document.createElement("div");
    wrap.className = "th-code";
    wrap.setAttribute("data-lang", lang);

    const bar = document.createElement("div");
    bar.className = "th-code__bar";

    const langEl = document.createElement("span");
    langEl.className = "th-code__lang";
    langEl.textContent = lang;

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "th-code__copy";
    btn.setAttribute("aria-label", "Скопировать код");
    setButton(btn, copyIcon(), "Copy");

    bar.appendChild(langEl);
    bar.appendChild(btn);

    pre.parentNode.insertBefore(wrap, pre);
    wrap.appendChild(bar);
    wrap.appendChild(pre);

    btn.addEventListener("click", () => {
        const text = code.innerText;
        const done = () => {
            btn.classList.add("is-copied");
            setButton(btn, checkIcon(), "Copied");
            setTimeout(() => {
                btn.classList.remove("is-copied");
                setButton(btn, copyIcon(), "Copy");
            }, 1800);
        };
        const fallback = () => {
            const ta = document.createElement("textarea");
            ta.value = text;
            ta.style.position = "fixed";
            ta.style.opacity = "0";
            document.body.appendChild(ta);
            ta.select();
            try { document.execCommand("copy"); } catch (_) {}
            document.body.removeChild(ta);
            done();
        };
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(text).then(done).catch(fallback);
        } else {
            fallback();
        }
    });
}

/** Красит блок, когда он подходит к экрану; без IntersectionObserver — сразу. */
function scheduleHighlight(code) {
    if (!highlighter) {
        highlight(code);
        return;
    }
    code.dataset.thHighlight = "pending";
    highlighter.observe(code);
}

function highlight(code) {
    Prism.highlightElement(code);
    code.dataset.thHighlight = "done";
}

function createHighlighter() {
    if (!("IntersectionObserver" in window)) return null;
    return new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            highlight(entry.target);
        });
    }, { rootMargin: HIGHLIGHT_AHEAD });
}

// Вызывается из pageInit() на каждой странице и после восстановления из bfcache
// (обработчик pageshow один, в index.js), поэтому своих слушателей не заводит.
// Наблюдатель подсветки пересоздаётся: блоки прошлой страницы ему больше не нужны.
export default function codeBlocks() {
    if (highlighter) highlighter.disconnect();
    highlighter = createHighlighter();
    document.querySelectorAll(".gh-content pre").forEach(enhance);
}
